import {
  formatPrice,
  generateSrcSet,
  safeParseJSON,
  extractIdFromGid,
} from '@theme/global-utilities';
import { createNodesChunkedProductsService } from '@theme/storefront-api';
import { PRODUCT_TILE_NODES_QUERY } from '@theme/product-queries';
import { SwatchCarousel } from '@theme/swatch-carousel';
 
/**
 * @typedef {Object} ProductImage
 * @property {string} src
 * @property {number=} width
 * @property {number=} height
 * @property {string=} alt
 */

/**
 * @typedef {HTMLElement & {
 *   productData?: ProductCardData,
 *   _wishlistSnapshot?: ProductCardData,
 *   viewModel?: ProductCardViewModel,
 *   domPatcher?: ProductCardDOMPatcher,
 *   swatchController?: ProductSwatchController,
 *   setup?: (data: ProductCardData) => Promise<void>
 *   _swatchCarousel?: SwatchCarousel
 * }} ProductCardComponentType
 */

/**
 * @typedef {Object} ProductFlags
 * @property {string[] | null} productBadge - Array of badge strings or null if none.
 */

/**
 * @typedef {Object} ProductSelectedVariant
 * @property {string} id
 * @property {string|null} sku
 * @property {string} title
 * @property {string} barcode
 * @property {boolean} availableForSale
 */

/**
 * @typedef {Object} ProductCardData
 * @property {string} id
 * @property {string} handle
 * @property {number=} totalInventory
 * @property {string} title
 * @property {string} url
 * @property {boolean=} availableForSale
 * @property {string[]=} tags
 * @property {string=} productType
 * @property {string|number|null} price
 * @property {string|number|null} compareAtPrice
 * @property {string|number|null=} reviewsAverage
 * @property {ProductImage=} image
 * @property {ProductFlags=} flags
 * @property {Array<ProductSwatch>=} swatches
 * @property {ProductImage=} image
 * @property {ProductImage=} hoverImage
 * @property {string[]=} collections
 * @property {ProductSelectedVariant|null=} selectedVariant
 */

/**
 * @typedef {Object} ProductCardVM
 * @property {HTMLImageElement|null} $image
 * @property {HTMLImageElement|null} $hoverImage
 * @property {HTMLAnchorElement|null} $link
 * @property {HTMLElement|null} $title
 * @property {HTMLElement|null} $reviews
 * @property {HTMLElement|null} $price
 * @property {HTMLElement|null} $badgeLabel
 * @property {HTMLElement|null} $productLabel
 * @property {HTMLElement|null} $swatchList
 * @property {HTMLElement|null} $swatchTemplate
 * @property {HTMLElement|null} $infoFooter
 * @property {HTMLScriptElement|null} $variantJson
 * @property {HTMLElement|null} $wishlistButton
 */

/**
 * @typedef {Object} ProductSelectedVariantNode
 * @property {string} id
 * @property {string|null} sku
 * @property {string} title
 * @property {string} barcode
 * @property {boolean} availableForSale
 */

/**
 * Product node shape from Storefront API `nodes(...)` query (subset).
 * @typedef {Object} ProductMetadataNode
 * @property {string} id
 * @property {string} handle
 * @property {string} title
 * @property {string} productType
 * @property {boolean} availableForSale
 * @property {number} totalInventory
 * @property {string[]=} tags
 * @property {string=} onlineStoreUrl
 * @property {string=} publishedAt
 * @property {{ nodes?: Array<{url?: string, width?: number, height?: number, altText?: string}> }} [images]
 * @property {{ maxVariantPrice?: { amount?: string, currencyCode?: string } }=} priceRange
 * @property {{ maxVariantPrice?: { amount?: string, currencyCode?: string } }=} compareAtPriceRange
 * @property {ProductMetafieldEntry[]=} metafields
 * @property {{ edges?: Array<{ node?: { id?: string, handle?: string } }> }} [collections]
 * @property {ProductSelectedVariantNode=} selectedOrFirstAvailableVariant
 */

/**
 * A single swatch reference rendered on the card.
 * @typedef {Object} ProductSwatch
 * @property {string=} productId
 * @property {string=} productHandle
 * @property {ProductCardData=} product
 * @property {boolean=} isChecked
 */

/**
 * Store of companion product data keyed by Product GID or `handle:{handle}`.
 * @typedef {Record<string, ProductCardData>} ProductDataStore
 */

/**
 * GraphQL metafield entry shape (subset).
 * @typedef {Object} ProductMetafieldEntry
 * @property {string} key
 * @property {string=} value
 */

/**
 * Options used to create a ProductSwatchController.
 * @typedef {Object} ProductSwatchControllerOptions
 * @property {HTMLElement} root
 * @property {ProductCardViewModel} viewModel
 * @property {ProductCardData} data
 * @property {(productData: ProductCardData) => void=} onVariantSelect
 */

let storefrontProductsService;

/**
 * Lazily create the Storefront API product service.
 *
 * Search & Discovery cards are rendered from Liquid and hydrate companion
 * products through Shopify's same-origin Ajax product endpoint, so they do not
 * require a Storefront API token. Bloomreach/minimal-ID cards still use this
 * service when they need GraphQL enrichment.
 *
 * @returns {ReturnType<typeof createNodesChunkedProductsService>}
 */
function getStorefrontProductsService() {
  if (!storefrontProductsService) {
    storefrontProductsService = createNodesChunkedProductsService({
      query: PRODUCT_TILE_NODES_QUERY,
      chunkSize: 25,
      flushDelay: 5,
    });
  }

  return storefrontProductsService;
}

/**
 * @param {string[]} productIds
 */
function fetchProductsBatched(productIds) {
  return getStorefrontProductsService().fetchProductsBatched(productIds);
}

const PRODUCT_SELECTORS = {
  IMAGE: '.image-button__image',
  HOVER_IMAGE: '.image-button__image.image-button__image--hover',
  LINK: '.image-button__link, .c-product-tile-decorator__content-wrapper',
  TITLE: '[data-replaceable-card-title]',
  REVIEWS: '[data-replaceable-card-reviews]',
  PRICE: '[data-replaceable-card-price]',
  BADGE_LABEL: '[data-replaceable-card-badge-label]',
  PRODUCT_LABEL: '[data-replaceable-card-product-label]',
  SWATCH_LIST: '.c-product-option__list',
  SWATCH_TEMPLATE: '.c-product-option__list .o-style-option__list-item',
  SWATCH_CLASS: 'color-selector',
  INFO_FOOTER_CLASS: 'c-product-tile__footer',
  COLOR_VALUE_WRAPPER: 'o-style-option__list-item',
  DATA_VARIANT_JSON: '[data-product-variant-json]',
  WISHLIST_BUTTON: '[data-swym-wishlist]',
};

const REVIEW_RATING_MAX = 5;
const REVIEW_STAR_PATH =
  'M9 14.118L14.562 17.475L13.086 11.148L18 6.891L11.529 6.342L9 0.375L6.471 6.342L0 6.891L4.914 11.148L3.438 17.475L9 14.118Z';

const COMING_SOON_BADGE = 'coming_soon';

let reviewRatingInstanceCounter = 0;

/**
 * Whether the card's selected variant currently has inventory. Prefers the
 * selected variant's availability, falling back to the product-level
 * availability contract used elsewhere in this file.
 * @param {ProductCardData} data
 * @returns {boolean}
 */
function hasSelectedInventory(data) {
  if (typeof data.selectedVariant?.availableForSale === 'boolean') {
    return data.selectedVariant.availableForSale;
  }
  if (typeof data.availableForSale === 'boolean') {
    return data.availableForSale;
  }
  return (data.totalInventory ?? 0) > 0;
}

/**
 * @param {string | null | undefined} sku
 * @returns {string | undefined}
 */
function getSkuItemId(sku) {
  if (!sku || typeof sku !== 'string') return undefined;
  const trimmed = sku.trim();
  if (!trimmed) return undefined;
  return trimmed.split(' ')[0] || undefined;
}

const PRODUCT_GID_PATTERN = /^gid:\/\/shopify\/Product\/\d+$/;
const PRODUCT_HANDLE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;
const ajaxProductCache = new Map();

function parsePublishedTimestamp(value) {
  if (typeof value !== 'string') return null;

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function isPublishedNow(value) {
  const timestamp = parsePublishedTimestamp(value);
  return timestamp !== null && timestamp <= Date.now();
}

function isPublishedForOnlineStore(product) {
  const hasOnlineStoreUrl =
    typeof product?.onlineStoreUrl === 'string'
    && product.onlineStoreUrl.trim().length > 0;

  return hasOnlineStoreUrl && isPublishedNow(product?.publishedAt);
}

/**
 * Password-protected storefronts can return published Product nodes with a
 * blank onlineStoreUrl. Those products need a same-origin Ajax verification
 * before they can be treated as Online Store-published.
 * @param {ProductMetadataNode | null | undefined} product
 * @returns {boolean}
 */
function needsOnlineStorePublicationVerification(product) {
  if (!product || typeof product !== 'object') return false;
  if (!isPublishedNow(product.publishedAt)) return false;
  if (isPublishedForOnlineStore(product)) return false;

  return normalizeProductHandle(product.handle) !== null;
}

/**
 * @param {unknown} id
 * @returns {string|null}
 */
function normalizeProductGid(id) {
  return typeof id === 'string' && PRODUCT_GID_PATTERN.test(id) ? id : null;
}

/**
 * @param {unknown} handle
 * @returns {string|null}
 */
function normalizeProductHandle(handle) {
  if (typeof handle !== 'string') return null;

  const trimmed = handle.trim();
  return PRODUCT_HANDLE_PATTERN.test(trimmed) ? trimmed : null;
}

/**
 * @param {string} handle
 * @returns {string}
 */
function getProductHandleStoreKey(handle) {
  return `handle:${handle}`;
}

/**
 * @param {ProductSwatch | null | undefined} swatch
 * @returns {string|null}
 */
function getSwatchProductId(swatch) {
  if (!swatch || typeof swatch !== 'object') return null;

  return normalizeProductGid(swatch.productId) || normalizeProductGid(swatch.product?.id);
}

/**
 * @param {ProductSwatch | null | undefined} swatch
 * @returns {string|null}
 */
function getSwatchProductHandle(swatch) {
  if (!swatch || typeof swatch !== 'object') return null;

  return normalizeProductHandle(swatch.productHandle) || normalizeProductHandle(swatch.product?.handle);
}

/**
 * @param {ProductSwatch | null | undefined} swatch
 * @param {ProductDataStore} dataStore
 * @returns {ProductCardData|null}
 */
function getProductDataForSwatch(swatch, dataStore) {
  const productId = getSwatchProductId(swatch);
  if (productId && dataStore[productId]) return dataStore[productId];

  const handle = getSwatchProductHandle(swatch);
  if (handle && dataStore[getProductHandleStoreKey(handle)]) {
    return dataStore[getProductHandleStoreKey(handle)];
  }

  return null;
}

/**
 * @param {ProductSwatch | null | undefined} swatch
 * @returns {ProductCardData|null}
 */
function getEmbeddedSwatchProduct(swatch) {
  const productId = getSwatchProductId(swatch);
  const product = swatch?.product;

  if (!productId || !product || typeof product !== 'object') return null;

  return {
    ...product,
    id: productId,
  };
}

/**
 * @param {ProductCardData | null | undefined} product
 * @param {ProductDataStore} dataStore
 * @returns {void}
 */
function saveProductData(product, dataStore) {
  if (!product || typeof product !== 'object') return;

  const productId = normalizeProductGid(product.id);
  if (productId) dataStore[productId] = product;

  const handle = normalizeProductHandle(product.handle);
  if (handle) dataStore[getProductHandleStoreKey(handle)] = product;
}

/**
 * @param {string} src
 * @param {Record<string, string|number>} params
 * @returns {string}
 */
function appendImageParams(src, params) {
  if (!src) return '';

  const separator = src.includes('?') ? '&' : '?';
  const query = Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');

  return `${src}${separator}${query}`;
}

/**
 * @param {number|null|undefined} cents
 * @returns {number|null}
 */
function centsToAmount(cents) {
  return typeof cents === 'number' && Number.isFinite(cents)
    ? cents / 100
    : null;
}

/**
 * @param {string} handle
 * @returns {Promise<ProductCardData|null>}
 */
async function fetchProductByHandle(handle) {
  const normalizedHandle = normalizeProductHandle(handle);
  if (!normalizedHandle) return null;

  if (ajaxProductCache.has(normalizedHandle)) {
    return ajaxProductCache.get(normalizedHandle);
  }

  const root = window.Theme?.routes?.root || window.Shopify?.routes?.root || '/';
  const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
  const url = `${normalizedRoot}products/${encodeURIComponent(normalizedHandle)}.js`;

  const promise = fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Unable to fetch product by handle: ${normalizedHandle}`);
      }
      return response.json();
    })
    .then(transformAjaxProductToCardData)
    .catch((error) => {
      ajaxProductCache.delete(normalizedHandle);
      throw error;
    });

  ajaxProductCache.set(normalizedHandle, promise);
  return promise;
}

/**
 * Normalize Shopify Ajax `/products/{handle}.js` product data into ProductCardData.
 * @param {any} product
 * @returns {ProductCardData|null}
 */
function transformAjaxProductToCardData(product) {
  if (!product || typeof product !== 'object' || !product.id || !product.handle) {
    return null;
  }

  if (!isPublishedNow(product.published_at)) {
    return null;
  }

  const productId = `gid://shopify/Product/${product.id}`;
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const selectedVariant =
    variants.find((variant) => variant?.available) || variants[0] || null;
  const images = Array.isArray(product.images) ? product.images : [];
  const mainImage = product.featured_image || images[0] || '';
  const hoverImage = images[1] || '';

  return {
    id: productId,
    handle: product.handle,
    title: product.title || '',
    productType: product.type || '',
    url: `${window.Theme?.routes?.root || '/'}products/${product.handle}`,
    availableForSale: product.available,
    tags: Array.isArray(product.tags) ? product.tags : [],
    price: centsToAmount(product.price),
    compareAtPrice: centsToAmount(product.compare_at_price),
    reviewsAverage: null,
    image: mainImage
      ? {
          src: mainImage,
          width: 500,
          height: 500,
          alt: product.title || '',
        }
      : undefined,
    hoverImage: hoverImage
      ? {
          src: hoverImage,
          width: 500,
          height: 500,
          alt: product.title || '',
        }
      : undefined,
    flags: {
      productBadge: null,
    },
    collections: [],
    selectedVariant: selectedVariant
      ? {
          id: `gid://shopify/ProductVariant/${selectedVariant.id}`,
          sku: selectedVariant.sku ?? null,
          title: selectedVariant.title || '',
          availableForSale: !!selectedVariant.available,
          barcode: selectedVariant.barcode ?? '',
        }
      : null,
  };
}

/**
 * @param {string | number | null | undefined} value
 * @returns {number | null}
 */
function normalizeReviewAverage(value) {
  if (value === null || value === undefined || value === '') return null;

  const rating = Number.parseFloat(String(value).trim());
  if (!Number.isFinite(rating) || rating <= 0) return null;

  return Math.min(REVIEW_RATING_MAX, Math.max(0, rating));
}

/**
 * @param {string | number | null | undefined} value
 * @returns {boolean}
 */
function hasReviewAverage(value) {
  return normalizeReviewAverage(value) !== null;
}

/**
 * @param {number} rating
 * @returns {string}
 */
function formatReviewAverage(rating) {
  return (Math.round(rating * 10) / 10).toFixed(1);
}

/**
 * @param {number} rating
 * @param {number} index
 * @returns {number}
 */
function getReviewStarFillPercent(rating, index) {
  const starValue = rating - index;

  if (starValue >= 1) return 100;
  if (starValue <= 0) return 0;

  return Math.round(starValue * 100);
}

class ProductCardViewModel {
  /** @type {HTMLElement} */
  $scope;
  /** @type {ProductCardVM} */
  vm;

  /** @param {HTMLElement} scope */
  constructor(scope) {
    this.$scope = scope;
    this.vm = this.getViewModel();
  }

  /** @returns {ProductCardVM} */
  getViewModel() {
    return {
      $image: this.$scope.querySelector(PRODUCT_SELECTORS.IMAGE),
      $hoverImage: this.$scope.querySelector(PRODUCT_SELECTORS.HOVER_IMAGE),
      $link: this.$scope.querySelector(PRODUCT_SELECTORS.LINK),
      $title: this.$scope.querySelector(PRODUCT_SELECTORS.TITLE),
      $reviews: this.$scope.querySelector(PRODUCT_SELECTORS.REVIEWS),
      $price: this.$scope.querySelector(PRODUCT_SELECTORS.PRICE),
      $badgeLabel: this.$scope.querySelector(PRODUCT_SELECTORS.BADGE_LABEL),
      $productLabel: this.$scope.querySelector(PRODUCT_SELECTORS.PRODUCT_LABEL),
      $swatchList: this.$scope.querySelector(PRODUCT_SELECTORS.SWATCH_LIST),
      $variantJson: this.$scope.querySelector(
        PRODUCT_SELECTORS.DATA_VARIANT_JSON
      ),
      $swatchTemplate: this.$scope.querySelector(
        PRODUCT_SELECTORS.SWATCH_TEMPLATE
      ),
      $infoFooter: this.$scope.querySelector(
        `.${PRODUCT_SELECTORS.INFO_FOOTER_CLASS}`
      ),
      $wishlistButton: this.$scope.querySelector(
        PRODUCT_SELECTORS.WISHLIST_BUTTON
      ),
    };
  }
}

class ProductCardDOMPatcher {
  /** @type {ProductCardComponent} */
  scope;
  /** @type {ProductCardVM} */
  viewModel;
  /** @type {string} */
  reviewGradientIdPrefix;
  /** @type {(e: Event) => void} */
  handleWishlistClickBound;

  /** @param {ProductCardComponent} scope */
  constructor(scope) {
    this.scope = scope;
    this.viewModel = scope.viewModel
      ? scope.viewModel.vm
      : /** @type {ProductCardVM} */ ({});
    this.reviewGradientIdPrefix = `product-card-review-${reviewRatingInstanceCounter++}`;
    this.handleWishlistClickBound = this.handleWishlistClick.bind(this);
  }

  /** @param {ProductCardData} data */
  render(data) {
    this.setLink(data);
    this.renderTitle(data);
    this.renderImage(data);
    this.renderReviews(data);
    this.renderPrice(data);
    this.renderBadgeLabel(data);
    this.renderProductLabel(data);
    this.updateProductCardJSON(data);
    this.renderWishlist(data);
  }

  /** @param {ProductCardData} data */
  updateProductCardJSON(data) {
    const { $variantJson } = this.viewModel;

    if ($variantJson) {
      $variantJson.textContent = JSON.stringify(data.selectedVariant);
    }
  }

  /** @param {ProductCardData} data */
  setLink(data) {
    const { $link } = this.viewModel;
    if ($link && data?.url) $link.href = data.url;
  }

  /** @param {ProductCardData} data */
  renderImage(data) {
    const { $image, $hoverImage, $title } = this.viewModel;
    const main = data?.image;
    const hover = data?.hoverImage;
    const prioritizeMainImage = this.scope.hasAttribute('data-priority-image');

    if ($image && main?.src) {
      this.applyResponsiveImg(
        $image,
        main,
        $title?.textContent || data.title || '',
        { prioritize: prioritizeMainImage }
      );
    }

    if ($hoverImage) {
      if (hover?.src) {
        this.applyResponsiveImg($hoverImage, hover, '');
        $hoverImage.setAttribute('aria-hidden', 'true');
        $hoverImage.hidden = false;

        // quick preload (non-blocking)
        try {
          const pre = new Image();
          pre.decoding = 'async';
          pre.loading = 'eager';
          pre.src = $hoverImage.currentSrc || $hoverImage.src || hover.src;
        } catch {}
      } else {
        // keep in DOM but hide if no hover image exists
        $hoverImage.hidden = true;
        $hoverImage.removeAttribute('src');
        $hoverImage.removeAttribute('srcset');
        $hoverImage.removeAttribute('sizes');
      }
    }
  }

  /**
   * Small helper to apply responsive src/srcset/sizes consistently.
   * @param {HTMLImageElement} imgEl
   * @param {ProductImage} imgData
   * @param {string} fallbackAlt
   * @param {{ prioritize?: boolean }=} options
   */
  applyResponsiveImg(imgEl, imgData, fallbackAlt, options = {}) {
    if (typeof generateSrcSet === 'function') {
      const widths = [165, 360, 533, 720, 940, 1000];
      const sizes =
        '(min-width: 1440px) 1000vw, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)';
      const {
        src,
        srcset,
        sizes: sizeAttr,
      } = generateSrcSet(imgData.src, widths, '533', sizes);
      imgEl.src = src;
      imgEl.srcset = srcset;
      imgEl.sizes = sizeAttr || '';
    } else {
      imgEl.src = imgData.src;
    }

    imgEl.alt = imgData.alt || fallbackAlt || '';
    if (imgData.width) imgEl.width = imgData.width;
    if (imgData.height) imgEl.height = imgData.height;

    if (options.prioritize) {
      imgEl.loading = 'eager';
      imgEl.setAttribute('fetchpriority', 'high');
      if ('fetchPriority' in imgEl) {
        imgEl.fetchPriority = 'high';
      }
    } else {
      imgEl.loading = imgEl.loading || 'lazy';
    }
    imgEl.decoding = imgEl.decoding || 'async';
  }

  /** @param {ProductCardData} data */
  renderTitle(data) {
    const { $title } = this.viewModel;
    if (!$title) return;
    $title.textContent = data?.title || '';
  }

  /** @param {ProductCardData} data */
  renderReviews(data) {
    const { $reviews } = this.viewModel;
    if (!$reviews) return;

    if (this.scope.hasAttribute('data-hide-reviews')) {
      $reviews.innerHTML = '';
      $reviews.hidden = true;
      return;
    }

    const rating = normalizeReviewAverage(data?.reviewsAverage);
    if (rating === null) {
      $reviews.innerHTML = '';
      $reviews.hidden = true;
      return;
    }

    const displayRating = formatReviewAverage(rating);
    const stars = Array.from({ length: REVIEW_RATING_MAX }, (_, index) =>
      this.renderReviewStar(rating, index)
    ).join('');

    $reviews.hidden = false;
    $reviews.innerHTML = `
      <span class="c-product-rating" role="img" aria-label="Rated ${displayRating} out of ${REVIEW_RATING_MAX}">
        <span class="c-product-rating__stars" aria-hidden="true">${stars}</span>
        <span class="c-product-rating__score">${displayRating}</span>
      </span>`;
  }

  /**
   * @param {number} rating
   * @param {number} index
   * @returns {string}
   */
  renderReviewStar(rating, index) {
    const fillPercent = getReviewStarFillPercent(rating, index);
    const gradientId = `${this.reviewGradientIdPrefix}-${index}`;

    return `<svg aria-hidden="true" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" class="c-product-rating__star" width="15" height="15">
      <defs>
        <linearGradient id="${gradientId}">
          <stop offset="${fillPercent}%" stop-color="rgba(0,0,0,1)"></stop>
          <stop offset="${fillPercent}%" stop-color="#FFFFFF"></stop>
        </linearGradient>
      </defs>
      <path d="${REVIEW_STAR_PATH}" stroke="currentColor" fill="url(#${gradientId})"></path>
    </svg>`;
  }

  /** @param {ProductCardData} data */
  renderPrice(data) {
    const { $price } = this.viewModel;
    if (!$price) return;

    const main = formatPrice(Shopify.currency.active, data.price);
    const cmp = formatPrice(Shopify.currency.active, data.compareAtPrice) || '';

    $price.innerHTML =
      cmp && String(cmp) !== String(main)
        ? `<div class="c-product-price c-product-tile__product-price">
             <span class="c-product-price__formatted-price">${main}</span>
             <span class="c-product-price__formatted-price--discounted">${cmp}</span>
           </div>`
        : `<div class="c-product-price c-product-tile__product-price">
             <span class="c-product-price__formatted-price">${main}</span>
           </div>`;
  }

  /** @param {ProductCardData} data */
  renderBadgeLabel(data) {
    const { $badgeLabel } = this.viewModel;
    if (!$badgeLabel) return;

    const firstBadge = Array.isArray(data.flags?.productBadge)
      ? data.flags.productBadge[0]
      : null;

    // Coming Soon requires manual removal: hide it automatically once the
    // selected variant has inventory again (admins are slow to unset the
    // badge). Only the coming_soon value is gated; other badges are unchanged.
    if (firstBadge === COMING_SOON_BADGE && hasSelectedInventory(data)) {
      $badgeLabel.innerHTML = '';
      return;
    }

    if (firstBadge) {
      const badgeText =
        window?.translations?.product?.badges?.[firstBadge] || firstBadge;

      $badgeLabel.innerHTML = `<span class="c-product-tile__badge-label eyebrow">${badgeText}</span>`;
    } else {
      $badgeLabel.innerHTML = '';
    }
  }

  /** @param {ProductCardData} data */
  renderProductLabel(data) {
    const { $productLabel } = this.viewModel;
    if (!$productLabel) return;

    const promotions = window.Promotions.getPromoMessages(data.collections || []);
    $productLabel.innerHTML = promotions.html;
  }

  /**
   * @param {ProductSwatch[] | undefined} swatches
   * @param {ProductDataStore} dataStore
   * @param {string} initialId
   */
  renderSwatches(swatches, dataStore, initialId) {
    const { $swatchList, $swatchTemplate } = this.viewModel;

    if (!$swatchList || !$swatchTemplate) return;

    const items = Array.isArray(swatches)
      ? swatches.filter((swatch) => getSwatchProductId(swatch) || getSwatchProductHandle(swatch))
      : [];
    let renderedCount = 0;
    const renderedProductIds = new Set();

    $swatchList.innerHTML = '';

    items.forEach((swatch) => {
      const productData = getProductDataForSwatch(swatch, dataStore);

      if (!productData) return;

      const productId = productData.id;
      if (!productId || renderedProductIds.has(productId)) return;

      const isChecked = productId === initialId;

      /** @type {HTMLElement} */
      const clone = /** @type {HTMLElement} */ (
        $swatchTemplate.cloneNode(true)
      );
      const button = clone.querySelector('button');
      const img = clone.querySelector('img');

      if (button) {
        button.setAttribute('data-product-id', productId);
        button.setAttribute('data-color-label', productData.title);
        button.setAttribute('data-swatch-image', productData.image?.src ?? '');
        if (productData?.selectedVariant?.id) {
          button.setAttribute('data-variant-id', productData.selectedVariant.id);
        }
        if (productData?.selectedVariant?.sku) {
          button.setAttribute('data-variant-sku', productData.selectedVariant.sku);
        }
        if (productData?.selectedVariant?.title) {
          button.setAttribute('data-variant-title', productData.selectedVariant.title);
        }

        if (isChecked) button.classList.add('is-checked');
      }

      if (img && productData.image?.src) {
        img.src = appendImageParams(productData.image.src, {
          height: 64,
          width: 64,
        });
        img.alt = productData.title;
        img.loading = 'lazy';
      }

      if (isChecked) {
        $swatchList.prepend(clone);
      } else {
        $swatchList.appendChild(clone);
      }

      renderedProductIds.add(productId);
      renderedCount++;
    });

    $swatchList.toggleAttribute('hidden', renderedCount < 2);
  }

  /** @param {ProductCardData} data */
  renderWishlist(data) {
    const btn = this.viewModel.$wishlistButton;
    if (!btn) return;

    this.scope._wishlistSnapshot = data;

    btn.dataset.empi = extractIdFromGid(data.id) ?? undefined;
    btn.dataset.epi =
      extractIdFromGid(data.selectedVariant?.id ?? '') ?? undefined;
    btn.dataset.du = `https://prod-colehaan.myshopify.com/${data.url}`;

    if (!btn.dataset.bound) {
      btn.dataset.bound = '1';
      btn.addEventListener('click', this.handleWishlistClickBound);
    }

    if (!window.swymcsWishlistFunctions) return;
    window.swymcsWishlistFunctions.isProductInWishlist(btn);
  }

  /** @param {Event} e */
  handleWishlistClick(e) {
    if (!(e.currentTarget instanceof HTMLElement)) return;

    e.preventDefault();

    const btn = e.currentTarget;
    const empi = btn.dataset.empi;
    const epi = btn.dataset.epi;

    if (!window.swymcsWishlistFunctions) return;

    if (!empi || !epi) {
      console.warn('[Wishlist] Missing empi/epi');
      return;
    }

    const data = this.scope._wishlistSnapshot;
    const wasActive = btn.classList.contains('is-active');

    window.swymcsWishlistFunctions.addToWishlist({
      empi,
      epi,
      du: btn.dataset.du,
      cprops: JSON.stringify({
        swatches: this.scope.swatchController?.productData?.swatches,
      }),
    });

    if (wasActive || !data) return;

    const events = window.ShopAnalytics?.events;
    if (!events?.addToWishlist) return;

    const productId = extractIdFromGid(data.id) ?? data.id;
    const variantId =
      extractIdFromGid(data.selectedVariant?.id ?? '') ??
      data.selectedVariant?.id;
    const skuItemId = getSkuItemId(data.selectedVariant?.sku);
    const priceValue =
      typeof data.price === 'number' && Number.isFinite(data.price)
        ? data.price
        : undefined;

    const product = {
      id: productId,
      styleId: skuItemId,
      title: data.title,
      type: data.productType,
      price: priceValue,
      currency: window.Shopify?.currency?.active,
    };

    const variant = variantId
      ? {
          id: variantId,
          title: data.selectedVariant?.title,
          sku: data.selectedVariant?.sku ?? undefined,
          barcode: data.selectedVariant?.barcode ?? undefined,
          price: priceValue,
        }
      : undefined;

    events.addToWishlist({
      product,
      variant,
      quantity: 1,
      source: 'product_card',
    });
  }

  hideSwatches() {
    const { $swatchList } = this.viewModel;

    $swatchList?.setAttribute('hidden', '');
  }

  initialCardBuildCleanUp() {
    const { $infoFooter } = this.viewModel;

    if ($infoFooter && $infoFooter.classList.contains('hidden')) {
      $infoFooter.classList.remove('hidden');
    }
  }
}

class ProductSwatchController {
  /** @type {ProductCardComponentType} */
  root;
  /** @type {ProductCardVM} */
  viewModel;
  /** @type {ProductCardData} */
  productData;
  /** @type {string} */
  initialId;
  /** @type {((productData: ProductCardData) => void) | undefined} */
  onVariantSelect;
  /** @type {NodeListOf<HTMLElement> | null} */
  _swatchButtons = null;
  /** @type {ProductDataStore} */
  dataStore;
  /** @type {(e: Event) => void} */
  handleSwatchClickBound;

  /** @param {ProductSwatchControllerOptions} opts */
  constructor({ root, viewModel, data, onVariantSelect }) {
    this.root = root;
    this.viewModel = viewModel.vm;
    this.productData = data;

    this.initialId = this.productData.id;
    this.onVariantSelect = onVariantSelect;
    this.dataStore = {};
    this.handleSwatchClickBound = this.handleSwatchClick.bind(this);
  }

  /** @returns {Promise<void>} */
  async initialize() {
    this.saveInitialProductData();
    this.saveEmbeddedSwatchProductData();

    const productHandles = this.extractProductHandles();
    if (productHandles.length > 0) {
      try {
        const products = await this.fetchProductsByHandle(productHandles);
        products.forEach((product) => saveProductData(product, this.dataStore));
      } catch (error) {
        console.warn('[ProductCard] Unable to fetch companion products by handle', error);
      }
    }

    const productIds = this.extractProductIDs();

    if (productIds.length > 0) {
      try {
        const metadata = await this.fetchProductMetadata(productIds);
        const unifiedData = this.tranformMetadata(metadata);
        const handleVerifiedData = await this.fetchHandleVerifiedMetadata(
          metadata
        );

        this.dataStore = {
          ...this.dataStore,
          ...unifiedData,
          ...handleVerifiedData,
        };
      } catch (error) {
        console.warn('[ProductCard] Unable to fetch companion products', error);
      }
    }

    this.applyBloomreachReviewFallbacks();

    if (this.hasSwatches()) {
      this.root.domPatcher?.renderSwatches(
        this.productData.swatches,
        this.dataStore,
        this.initialId
      );

      this._swatchButtons = null;
      this.processSwatches();
      this.attachSwatchListeners();
      this.initSwatchCarousel();
    } else {
      // Hide swatches if only 1 or doesn't exist.
      this.root.domPatcher?.hideSwatches();
    }
  }

  /** @returns {void} */
  applyBloomreachReviewFallbacks() {
    if (!this.root.hasAttribute('data-bloomreach-card')) return;
    if (!hasReviewAverage(this.productData?.reviewsAverage)) return;

    Object.values(this.dataStore).forEach((productData) => {
      if (!productData) return;

      productData.reviewsAverage = this.productData.reviewsAverage;
    });
  }

  initSwatchCarousel() {
    const ul = this.viewModel.$swatchList;
    if (ul instanceof HTMLUListElement) {
      this.root._swatchCarousel?.destroy?.();
      this.root._swatchCarousel = new SwatchCarousel(
        { list: ul },
        {
          mobileMax: 3,
          desktopMax: 5,
        }
      );
      this.root._swatchCarousel.init();
    }
  }

  /** @returns {void} */
  saveInitialProductData() {
    saveProductData(this.productData, this.dataStore);
  }

  /** @returns {void} */
  saveEmbeddedSwatchProductData() {
    if (!Array.isArray(this.productData?.swatches)) return;

    this.productData.swatches.forEach((swatch) => {
      const productId = getSwatchProductId(swatch);
      const product = getEmbeddedSwatchProduct(swatch);

      if (productId && product) {
        saveProductData(product, this.dataStore);
      }
    });
  }

  /** @returns {boolean} */
  hasSwatches() {
    return Array.isArray(this.productData?.swatches)
      && this.productData.swatches.some(
        (swatch) => getSwatchProductId(swatch) || getSwatchProductHandle(swatch)
      );
  }

  /**
   * Converts raw GraphQL product metadata into `ProductCardData` map.
   * Note: `metadata` is expected to be an object keyed by Product GID.
   * @param {Record<string, ProductMetadataNode>} metadata
   * @returns {ProductDataStore}
   */
  tranformMetadata(metadata) {
    return Object.entries(metadata).reduce((acc, [id, product]) => {
      const transformed = transformProductMetadataToCardData(product);
      if (transformed) {
        acc[id] = transformed;
      }
      return acc;
    }, /** @type {ProductDataStore} */ ({}));
  }

  /**
   * Verify products whose Storefront node has no onlineStoreUrl through the
   * Online Store Ajax endpoint. A successful response proves the handle is
   * published there while still allowing password-protected stores to render
   * valid companion swatches.
   * @param {Record<string, ProductMetadataNode>} metadata
   * @returns {Promise<ProductDataStore>}
   */
  async fetchHandleVerifiedMetadata(metadata) {
    const candidates = Object.values(metadata).filter(
      needsOnlineStorePublicationVerification
    );

    if (candidates.length === 0) return {};

    const products = await this.fetchProductsByHandle(
      candidates.map((product) => product.handle)
    );
    const verifiedHandles = new Set(
      products
        .map((product) => normalizeProductHandle(product.handle))
        .filter(Boolean)
    );

    return candidates.reduce((acc, product) => {
      const handle = normalizeProductHandle(product.handle);
      if (!handle || !verifiedHandles.has(handle)) return acc;

      const transformed = formatProductMetadataToCardData(product);
      if (transformed) {
        saveProductData(transformed, acc);
      }

      return acc;
    }, /** @type {ProductDataStore} */ ({}));
  }

  /**
   * Fetch companion product metadata by IDs.
   * @param {string[]} productIds
   * @returns {Promise<Record<string, ProductMetadataNode>>}
   */
  async fetchProductMetadata(productIds) {
    const result = await fetchProductsBatched(productIds);

    const resultConverted = Object.fromEntries(
      Array.from(result.byId.entries())
    );

    return resultConverted;
  }

  /**
   * Fetch companion product metadata by handles through Shopify's same-origin Ajax endpoint.
   * @param {string[]} productHandles
   * @returns {Promise<ProductCardData[]>}
   */
  async fetchProductsByHandle(productHandles) {
    const results = await Promise.allSettled(
      productHandles.map((handle) => fetchProductByHandle(handle))
    );

    return results
      .filter((result) => result.status === 'fulfilled' && result.value)
      .map((result) => result.value);
  }

  /**
   * Get companion product handles from swatches not already present in the local data store.
   * @returns {string[]}
   */
  extractProductHandles() {
    if (!this.productData?.swatches) return [];

    return Array.from(
      new Set(
        this.productData.swatches
          .map((swatch) => getSwatchProductHandle(swatch))
          .filter((handle) => handle && !this.dataStore[getProductHandleStoreKey(handle)])
      )
    );
  }

  /**
   * Get companion product IDs from swatches (excluding the initial product id).
   * @returns {string[]}
   */
  extractProductIDs() {
    if (!this.productData?.swatches) return [];

    return Array.from(
      new Set(
        this.productData.swatches
          .map((swatch) => getSwatchProductId(swatch))
          .filter((id) => id && id !== this.initialId && !this.dataStore[id])
      )
    );
  }

  /** @returns {void} */
  processSwatches() {
    this.swatchButtons.forEach((swatch) => {
      const productId = swatch.getAttribute('data-product-id');
      if (!productId) return;

      const productData = this.dataStore[productId];

      if (productData) {
        const isAvailable =
          typeof productData.availableForSale === 'boolean'
            ? productData.availableForSale
            : (productData.totalInventory ?? 0) > 0;

        swatch.setAttribute('data-variant-handle', productData.handle);
        if (!isAvailable) this.markUnavailable(swatch);
      }
    });
  }

  /** @returns {void} */
  attachSwatchListeners() {
    this.swatchButtons.forEach((swatch) => {
      swatch.addEventListener('click', this.handleSwatchClickBound);
    });
  }

  /** @param {MouseEvent} event */
  handleSwatchClick(event) {
    if (!(event.currentTarget instanceof HTMLElement)) return;

    const swatch = event.currentTarget;

    const productId = swatch.getAttribute('data-product-id');
    if (!productId) return;

    const productData = this.dataStore[productId];

    if (!productData) return;

    if (!swatch.classList.contains('is-checked')) {
      this.updateSwatchSelection(swatch);
      this.onVariantSelect?.(productData);
    }
  }

  /** @param {HTMLElement} currentSwatch */
  updateSwatchSelection(currentSwatch) {
    this.swatchButtons.forEach((btn) => btn.classList.remove('is-checked'));
    currentSwatch.classList.add('is-checked');
  }

  /** @param {HTMLElement} swatch */
  markUnavailable(swatch) {
    swatch.classList.add('is-unavailable');
  }

  /** @returns {void} */
  detachSwatchListeners() {
    this.swatchButtons.forEach((swatch) => {
      swatch.removeEventListener('click', this.handleSwatchClickBound);
    });
  }

  /**
   * Lazily initialize swatch buttons.
   * Returns cached NodeList if it already exists.
   * @returns {NodeListOf<HTMLElement>}
   */
  get swatchButtons() {
    if (!this._swatchButtons) {
      this._swatchButtons = /** @type {NodeListOf<HTMLElement>} */ (
        this.viewModel.$swatchList?.querySelectorAll(
          `.${PRODUCT_SELECTORS.SWATCH_CLASS}`
        ) || []
      );
    }
    return this._swatchButtons;
  }
}

class ProductCardComponent extends HTMLElement {
  connectedCallback() {
    const jsonAttr = this.getAttribute('data-json');
    const jsonScript = this.querySelector(
      'script[type="application/json"][data-product-card-json]'
    );

    if (!this.querySelector('.c-product-tile')) {
      const skeleton = document.querySelector('#product-card-placeholder');
      if (skeleton) this.innerHTML = skeleton.innerHTML;
    }

    if (this._productData) {
      this.setup(this._productData);
      return;
    }

    if (jsonAttr) {
      try {
        this.productData = JSON.parse(jsonAttr);
        this.removeAttribute('data-json');
        return;
      } catch (e) {
        console.warn('Invalid JSON in data-json attribute', e, jsonAttr);
      }
    }

    if (jsonScript) {
      try {
        this.productData = JSON.parse(jsonScript.textContent || '{}');
      } catch (e) {
        console.warn('Invalid JSON in <script>', e, jsonScript.textContent);
      }
    }
  }

  /** @param {ProductCardData} data */
  set productData(data) {
    this._productData = data;
    this.setup(data);
  }

  /** @returns {ProductCardData|undefined} */
  get productData() {
    return this._productData;
  }

  /** @param {ProductCardData} data */
  async setup(data) {
    this.viewModel = new ProductCardViewModel(this);
    this.domPatcher = new ProductCardDOMPatcher(this);

    const enriched = await enrichIfMinimal(data);

    this.swatchController = new ProductSwatchController({
      root: this,
      viewModel: this.viewModel,
      data: enriched,
      /** @param {ProductCardData} productData */
      onVariantSelect: (productData) => {
        this.domPatcher?.render(productData);
      },
    });

    this.domPatcher.render(enriched);
    this.domPatcher.initialCardBuildCleanUp();
    await this.swatchController.initialize();
  }

  disconnectedCallback() {
    this.swatchController?.detachSwatchListeners();
    // @ts-ignore
    this._swatchCarousel?.destroy?.();
  }
}

customElements.define('product-card-custom', ProductCardComponent);

/**
 * Normalize a Storefront Product node into ProductCardData.
 * @param {ProductMetadataNode | null | undefined} product
 * @returns {ProductCardData | null}
 */
function transformProductMetadataToCardData(product) {
  if (!product || typeof product !== 'object') return null;
  if (!isPublishedForOnlineStore(product)) return null;

  return formatProductMetadataToCardData(product);
}

/**
 * Normalize an already publication-verified Storefront Product node into
 * ProductCardData.
 * @param {ProductMetadataNode | null | undefined} product
 * @returns {ProductCardData | null}
 */
function formatProductMetadataToCardData(product) {
  if (!product || typeof product !== 'object') return null;

  /** @type {ProductMetafieldEntry[]} */
  const metafields = Array.isArray(product.metafields)
    ? product.metafields
    : [];

  /** @type {ProductImage[]} */
  const images = Array.isArray(product.images?.nodes)
    ? product.images.nodes
        .filter((n) => !!n?.url)
        .map((n) => ({
          src: n.url || '',
          width: typeof n.width === 'number' ? n.width : 500,
          height: typeof n.height === 'number' ? n.height : 500,
          alt: n.altText || '',
        }))
    : [];

  /** @type {(key: string) => (string|null)} */
  const getMetafieldValue = (key) => {
    const entry = metafields.find((m) => m?.key === key);
    return entry?.value ?? null;
  };

  /** @type {string|null} */
  const price = product.priceRange?.maxVariantPrice?.amount
    ? `$${parseFloat(product.priceRange.maxVariantPrice.amount)}`
    : null;

  /** @type {string|null} */
  const compareAtPrice =
    product.compareAtPriceRange?.maxVariantPrice?.amount &&
    parseFloat(product.compareAtPriceRange.maxVariantPrice.amount) !== 0
      ? `$${parseFloat(product.compareAtPriceRange.maxVariantPrice.amount)}`
      : null;

  /** @type {string|null} */
  const reviewsAverage = getMetafieldValue('reviews_average');

  /** @type {string[] | null} */
  const productBadge = safeParseJSON(getMetafieldValue('product_badge'));

  /** @type {string[]} */
  const collections = Array.isArray(product.collections?.edges)
    ? /** @type {string[]} */ (
        product.collections.edges
          .map((edge) => edge?.node?.handle)
          .filter(
            (handle) => typeof handle === 'string' && handle.trim().length > 0
          )
      )
    : [];

  const sv = product.selectedOrFirstAvailableVariant;
  const selectedVariant = sv
    ? {
        id: sv.id,
        sku: sv.sku ?? null,
        title: sv.title,
        availableForSale: !!sv.availableForSale,
        barcode: sv.barcode ?? '',
      }
    : null;

  const flags = {
    productBadge,
  };

  /** @type {ProductCardData} */
  const formatted = {
    id: product.id,
    handle: product.handle,
    title: product.title,
    productType: product.productType,
    url: Theme.routes.root + `products/${product.handle}`,
    availableForSale: product.availableForSale,
    totalInventory: product.totalInventory,
    tags: product.tags || [],
    price,
    compareAtPrice,
    reviewsAverage,
    image: images[0],
    hoverImage: images[1],
    flags,
    collections,
    selectedVariant,
  };

  return formatted;
}

/**
 * If product is minimal (id-only, wishlist), fetch metadata and merge into it.
 * Preserves any provided fields (e.g., swatches).
 * @param {ProductCardData} product
 * @returns {Promise<ProductCardData>}
 */
async function enrichIfMinimal(product) {
  // Handle should exist in full product payload
  if (product.handle) return product;

  try {
    const result = await fetchProductsBatched([product.id]);
    const byId = Object.fromEntries(Array.from(result.byId.entries()));
    const transformed = transformProductMetadataToCardData(byId[product.id]);

    return transformed ? { ...transformed, ...product } : product;
  } catch {
    return product;
  }
}
